import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material'
import { NotificationsNone } from '@mui/icons-material'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { ROUTES } from '@/utils/constants'
import { formatDateTimeDMY } from '@/utils/date'
import {
  notificationIcon,
  safeInternalNotificationUrl,
} from './notificationPresentation'

export function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const latest = useNotificationsStore((state) => state.latest)
  const unreadCount = useNotificationsStore((state) => state.unreadCount)
  const markRead = useNotificationsStore((state) => state.markRead)
  const markAllRead = useNotificationsStore((state) => state.markAllRead)

  const openNotification = async (id: string, url: string | null) => {
    const target = safeInternalNotificationUrl(url)
    if (!target) {
      return
    }
    try {
      await markRead(id)
    } finally {
      setAnchor(null)
      navigate(target)
    }
  }

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={t('notifications.bell')}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsNone />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 24px)' } } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontWeight={700} sx={{ flex: 1 }}>
            {t('notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={() => void markAllRead()}>
              {t('notifications.readAll')}
            </Button>
          )}
        </Box>
        <Divider />
        {latest.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            {t('notifications.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {latest.map((notification) => {
              const targetUrl = safeInternalNotificationUrl(notification.url)
              const secondary = (
                <>
                  <Typography component="span" variant="body2" display="block" noWrap>
                    {notification.body}
                  </Typography>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {formatDateTimeDMY(notification.created_at)}
                  </Typography>
                </>
              )

              if (!targetUrl) {
                return (
                  <Box
                    key={notification.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      px: 2,
                      py: 1.25,
                      bgcolor: notification.read_at ? undefined : 'action.hover',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                      {notificationIcon(notification.event_key)}
                    </ListItemIcon>
                    <ListItemText primary={notification.title} secondary={secondary} />
                  </Box>
                )
              }

              return (
                <ListItemButton
                  key={notification.id}
                  onClick={() =>
                    void openNotification(notification.id, notification.url)
                  }
                  sx={{ bgcolor: notification.read_at ? undefined : 'action.hover' }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {notificationIcon(notification.event_key)}
                  </ListItemIcon>
                  <ListItemText primary={notification.title} secondary={secondary} />
                </ListItemButton>
              )
            })}
          </List>
        )}
        <Divider />
        <Button
          fullWidth
          onClick={() => {
            setAnchor(null)
            navigate(ROUTES.NOTIFICATIONS)
          }}
          sx={{ py: 1 }}
        >
          {t('notifications.allNotifications')}
        </Button>
      </Menu>
    </>
  )
}
